/**
 * Admin guide 6 — route tests: test drops before the window, the formula
 * scores each gateway, the best one (past the "Use for the rest of today"
 * bar when there is one) takes the rest of the group's day. 21/10/2026: a
 * winner the gateway verdict blocks hands its containers to the next ranked
 * gateway; the ranking in order is on the Gateways tab and in the editor.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import type { DropGenEngineSettings } from '@trafficpulse/shared';

import { Flow, Points, V } from '../guide-ui';

export const RouteTestSection: FC<{ s: DropGenEngineSettings }> = ({ s }) => (
  <div className="flex flex-col gap-3">
    <Flow
      stamp="A route test's day"
      steps={[
        { when: '1 · Switch it on', what: 'Strategy Route tests', how: 'Master › Gateways, plus one enabled Route test container — its drops go before the window.' },
        { when: '2 · Test drops', what: 'Every gateway tried', how: 'Small drops to the pool’s gateways, taking turns, all with the same best texts.' },
        { when: '3 · The formula', what: 'Each gateway scored', how: 'Once every test drop is sent and settled — by default on Delivered rate today.' },
        { when: '4 · Rest of the day', what: 'The best one wins', how: 'It and its best sender take every unsent, not hand-edited draft of the group — new ones too.' },
      ]}
    />
    <Points
      items={[
        <>The formula does not choose <i>which</i> gateways are tested — the pool does. It ranks them on today's results.</>,
        <>No “Use for the rest of today” rule: the <b>best score wins</b>. With such a rule (for example Delivered rate above 80 %), only the gateways it holds for can win. “Not enough data” leaves out a gateway with no SMS.</>,
        <>Checked in every pass, <V>every {s.verdictEveryMin} min</V>. On its own drops the numbers are final after <V>{s.statsFreshHours} h</V>, so the winner settles; on the gateway's own stats it can change during the day, and the drafts follow it. A blocked gateway never wins again that day.</>,
        <>If the gateway verdict blocks the winner, the route test's <b>next gateway takes over</b> where it is blocked; if every ranked gateway is blocked there, its drafts take turns across the pool.</>,
        <>The ranking in order — every tested gateway with its score, sender and why one cannot win — is on the <b>Gateways tab</b>, and live in the route test editor next to its formula.</>,
        <>Before a winner, or when nothing can be ranked, ordinary drops take turns across the pool. Tomorrow starts over.</>,
      ]}
    />
    <p className="text-xs text-muted-foreground">Route tests are set per master: Gateways › strategy, and the Route test container’s formula and rules.</p>
  </div>
);
